// @givmo/board — the words of "Upload your board’s older files" (CBG-AI5-INBOX-WEB, D-CBG-41;
// design-284 `scratchpad/2026-10-24-import-inbox-design/design.md` §2, §8, frames u1–u4 and c4).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. The import page's line carrying the upload step (frame u1) is Jonathan's connector
// sentence (D-CBG-39 answer 3) as his approval of the design widens it (D-CBG-41, 2026-10-24 15:08
// PDT: "looks good, go with your recs", its question 1 answered yes). Every other line was a draft
// until the folder upload's copy round, CBG-AI5-INBOX-COPY1, approved it (D-CBG-46, 2026-10-29 21:31
// PDT: "1) go with yoru recs"; `scratchpad/2026-10-29-inbox-copy-round/copy-review.html`), each as
// recommended there. Each line names its row on that page. The Word file's lines (CBG-FIX160 H)
// were drafts until D-CBG-50 approved them as built (2026-10-31 08:06 PDT).
//
// D-CBG-45 as amended binds every line: where a line names the AI, it says "AI assistant", never the
// connected tool's name; "your AI assistant" only where a line tells the reader to ask their own, and
// "an AI assistant" for the one that proposed, since another importer's can.
//
// ⚠ No line names a batch (D-CBG-39): the set a connected AI's proposals wait in is a confirmation's
// internal detail. An uploaded file's name is the person's own and renders as plain text only.

/** "{n} record" / "{n} records". */
function records(n: number): string {
  return `${n} ${n === 1 ? 'record' : 'records'}`;
}

/** "{n} page" / "{n} pages". */
function pages(n: number): string {
  return `${n} ${n === 1 ? 'page' : 'pages'}`;
}

export const uploadWords = {
  // ── The import page's line (frame u1) ────────────────────────────────────────────────────────

  /** APPROVED (D-CBG-39 answer 3, widened by D-CBG-41; CBG-AI5-INBOX-COPY1's A1, as it stands):
   *  the line, its middle the upload page's link. Shown only while the uploads serve; otherwise the
   *  import page keeps the line it had. */
  importLineBefore: 'To process a whole folder of files at once, ',
  importLineLink: 'upload it here',
  importLineAfter:
    ', then use the Givmo connector in your AI tool (for example, Claude) to process them all, and you confirm them here.',

  // ── The page (frames u2–u4) ──────────────────────────────────────────────────────────────────

  /** Approved (CBG-AI5-INBOX-COPY1's B1): the title and its lead. */
  title: 'Upload your board’s older files',
  lead: 'Put your board’s older files here. Your AI assistant reads them through the Givmo connector and proposes a record for each board document it finds. Nothing goes into your minute book until you confirm it.',
  /** Approved (B2): the way back, naming the import page as the confirmation page's does. */
  backToImport: 'Back to Import older records',

  /** Approved (B3, B4): the drop zone, empty (u2) and once files are here (u3, u4). */
  dropHere: 'Drop a folder or files here',
  or: 'or',
  chooseFiles: 'Choose files',
  /** Approved (B4); a Word file since CBG-FIX160 H (D-CBG-47), approved in D-CBG-50. */
  whatItTakes:
    'PDFs, Word files or pictures (JPEG, PNG, HEIC or TIFF), up to 50 MB each. Only people in your organization who can import records, and the AI assistants they connect, can see them.',
  dropMore: 'Drop more files or folders here',
  /** Approved (B3; cut inbox's review fold, M8): the folder's own way in beside "Choose files", and
   *  what comes with it: every file in the folders inside it, each keeping its folder in its name. */
  chooseFolder: 'Choose a folder',
  foldersInside:
    'Files in folders inside it come too, each named with its folder, like “2019/Minutes.pdf”.',

  /** Approved (B5): the card beside it. */
  thenAsk: 'Then ask your AI assistant',
  /** Approved (B5; review fold, M2): when to ask, once every file is up, or the AI misses the last;
   *  and what to do with the sentence. */
  askWhen: 'Once everything has uploaded, copy this and send it to your AI assistant:',
  askSentence: 'Propose records for the files I uploaded to Givmo Board.',
  /** Approved (B5; review fold, M1): what the AI can't read until Givmo carries pictures to it. */
  whatTheAiReads:
    'Your AI assistant can’t read pictures yet, or scans with no text in them, so it may ask you what they say.',
  /** Approved (B5; review fold, M2): the organization's connected AI, on the console's own page. */
  seeWhatsConnected: 'See which AI assistants are connected',
  copy: 'Copy',
  /** Approved (B6): what "Copy" says once the sentence is on the clipboard, and when the browser
   *  refused it (the sentence stays on screen to select). */
  copied: 'Copied',
  couldNotCopy: 'We couldn’t copy it. Select the sentence and copy it yourself.',

  // ── A folder going up (frame u3) ─────────────────────────────────────────────────────────────

  /** Approved (B7): the count over the files going up. */
  uploadedOf: (done: number, total: number) => `${done} of ${total} uploaded`,
  /** Approved (B8; review fold, M4): the count once nothing is going up, and the parts it never
   *  calls uploaded, each said apart. */
  uploadedCount: (n: number) => `${n} uploaded`,
  cantBeAddedCount: (n: number) => `${n} can’t be added`,
  didNotUploadCount: (n: number) => `${n} didn’t upload`,
  /** Approved (B9; review fold, M4): the finish, plainly, every file Board takes being up. */
  allUploaded: 'All uploaded. Now ask your AI assistant.',
  restUploaded: 'The rest are uploaded. Now ask your AI assistant.',
  /** Approved (B7): a file going up, and Board checking it. */
  uploading: (percent: number) => `Uploading… ${percent}%`,
  checking: 'Checking…',
  /** Approved (B7, B8): a file still in line, a few going up at once; a file Board takes none of
   *  by its type; and the quiet flags. */
  queued: 'Waiting to upload',
  /** A Word file since CBG-FIX160 H (D-CBG-47), approved in D-CBG-50. */
  notAPdfOrPicture: 'Not a PDF, a Word file or a picture, so it can’t be added.',
  alreadyUploaded: 'Already uploaded',
  alreadyInTheBook: 'Already in your minute book',
  openTheRecord: 'Open the record',
  /** Approved (B8, B14; review fold, M6): two acts, two words. A waiting file's menu takes it off
   *  Board's uploads (never the computer's copy); a row Board took nothing for is only cleared. */
  remove: 'Remove from your uploads',
  clear: 'Clear',
  /** Approved (B8): the other ways a file can't be added, each in the line's own shape. */
  tooLarge: 'Larger than 50 MB, so it can’t be added.',
  unreadable: 'Board couldn’t read it, so it can’t be added.',
  didNotUpload: 'It didn’t upload.',
  /** Approved (B8; review fold, M5): each refusal's next step. */
  nextStepType: 'Save it as a PDF or a picture and add it again.',
  nextStepSize: 'Save a smaller copy, or split it, and add it again.',
  nextStepUnreadable: 'Save it again as a PDF or a picture, then add it.',
  tryAgain: 'Try again',
  /** Approved (B10): a folder over the uploads' limits, refused whole before anything uploads
   *  (design §3). The limits are the backend's own answer, written as it gives them. Proposing makes
   *  no room: a proposed file counts until its record is confirmed. */
  overTheLimits: (files: string, bytes: string) =>
    `Up to ${files} files and ${bytes} can wait in your uploads at once, so nothing in this folder was uploaded. Upload a smaller folder, or make room first: confirm the proposed records waiting for you, or remove files you don’t need.`,

  // ── The next morning (frame u4) ──────────────────────────────────────────────────────────────

  /** Approved (B11): the proposed group. The AI that acted is "an AI assistant", never its tool's
   *  name (D-CBG-45 as amended), since the reader may not be the person whose AI proposed. */
  proposedHeading: (n: number) => `Proposed, waiting for you (${n})`,
  proposedLead: (n: number) => `An AI assistant proposed ${records(n)} from these.`,
  reviewThem: 'Review them',
  /** Approved (B12): the files no record was proposed from yet, and the AI works only when asked. */
  waitingHeading: (n: number) => `Not proposed yet (${n})`,
  waitingLead:
    'Your AI assistant works on these only when you ask it. Ask it again, or remove any you don’t need.',
  /** Approved (B13): the files these uploads added to the minute book. */
  inTheBookHeading: (n: number) => `Added to your minute book (${n})`,
  /** Approved (B14): the row menu's way to open a file waiting or proposed. */
  openTheFile: 'Open the file',
  /** A Word file's, which downloads (CBG-FIX160 H, D-CBG-47); approved in D-CBG-50. */
  downloadTheFile: 'Download the file',
  /** Approved (B13): the minute book's group lists the most recent 200; the rest are there. */
  inTheBookMore: (shown: number, total: number) =>
    `The ${shown} most recently uploaded of ${total}. Every one is in your minute book.`,

  /** Approved (B7): one file's quiet line: its kind, size, pages (a PDF's) and upload date. */
  //  Its date is the upload's, said so (review fold, M3): beside a file named for a meeting's
  //  date, a bare date reads as the document's.
  fileLine: (kind: string, size: string, pageCount: number | null, uploaded: string | null) =>
    [kind, size, pageCount === null ? null : pages(pageCount), uploaded && `Uploaded ${uploaded}`]
      .filter((part) => part !== null)
      .join(' · '),
  /** Approved (B8): a file Board takes none of, by its kind (frame u3's "Other"). */
  otherKind: 'Other',

  /** Approved (B15): the page's reads and acts that failed, with "Try again". */
  couldNotLoad: 'We couldn’t load your uploads.',
  couldNotRemove: 'We couldn’t remove that file. Please try again.',
  couldNotOpen: 'We couldn’t open that file. Please try again.',

  // ── The confirmation page (frame c4) ─────────────────────────────────────────────────────────

  /** Approved (C1): under a file a person uploaded for the AI. */
  fromYourUploads: 'From your uploads',
} as const;
